import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { Col } from "module/common/component/layout";
import { CustomAnimationConfig } from "../Animated/Animated";

export interface DrawerProps {
    open: boolean;
    onClose?: () => void;
    position?: "left" | "right" | "top" | "bottom";
    children?: React.ReactNode;
    className?: string;
    animation?: CustomAnimationConfig; // Custom animation config
    size?: { width?: string; height?: string; mobileWidth?: string; mobileHeight?: string }; // Custom size
    name?: string; // Drawer name for management
    preventScroll?: boolean; // Prevent body scroll when open
    elevation?: number; // Shadow elevation level
    BackdropProps?: { transparent?: boolean; style?: React.CSSProperties }; // Backdrop configuration
}

const DrawerOverlay = styled.div<{ open: boolean }>`
    position: fixed;
    top: ${({ theme }) => theme.layout.headerHeight}px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: ${({ open }) => (open ? "block" : "none")};
    z-index: 1200;
`;

const DrawerContent = styled(Col)<{ position: string; open: boolean; size?: { width?: string; height?: string; mobileWidth?: string; mobileHeight?: string } }>`
    position: fixed;
    background-color: ${({ theme }) => theme.palette.white};
    z-index: 1201;
    transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);

    ${({ position, open, size, theme }) => {
        const width = size?.width || "300px";
        const height = size?.height || "300px";
        const mobileWidth = size?.mobileWidth;
        const mobileHeight = size?.mobileHeight;
        
        if (position === "left") {
            return css`
                top: ${theme.layout.headerHeight}px;
                left: 0;
                bottom: 0;
                width: ${width};
                transform: translateX(${open ? "0" : "-100%"});
                ${mobileWidth ? css`${theme.breakpoints.down("mobile")} { width: ${mobileWidth}; }` : ''}
            `;
        }
        if (position === "right") {
            return css`
                top: ${theme.layout.headerHeight}px;
                right: 0;
                bottom: 0;
                width: ${width};
                max-width: ${width};
                transform: translateX(${open ? "0" : "100%"});
                ${mobileWidth ? css`${theme.breakpoints.down("mobile")} { width: ${mobileWidth}; max-width: ${mobileWidth}; }` : ''}
            `;
        }
        if (position === "top") {
            return css`
                top: 0;
                left: 0;
                right: 0;
                height: ${height};
                transform: translateY(${open ? "0" : "-100%"});
                ${mobileHeight ? css`${theme.breakpoints.down("mobile")} { height: ${mobileHeight}; }` : ''}
            `;
        }
        return css`
            bottom: 0;
            left: 0;
            right: 0;
            height: ${height};
            transform: translateY(${open ? "0" : "100%"});
            ${mobileHeight ? css`${theme.breakpoints.down("mobile")} { height: ${mobileHeight}; }` : ''}
        `;
    }}
`;

export const Drawer: React.FC<DrawerProps> = ({ open, onClose, position = "right", children, size, className }) => {
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [open]);

    return (
        <>
            <DrawerOverlay open={open} onClick={onClose} />
            <DrawerContent position={position} open={open} size={size} className={className}>
                {children}
            </DrawerContent>
        </>
    );
};

export default Drawer;


